// Localização: frontend/src/components/Financeiro/ListaCategorias.jsx

import React from 'react';
import { useSearch } from '../../context/SearchContext';
import PrimaryButton from '../Common/PrimaryButton';
import { Folder, SquarePen, Trash2, CheckCircle, XCircle, TrendingUp, TrendingDown } from 'lucide-react';

// Componente de Card para Mobile
const CategoriaCard = ({ categoria, canEdit, canDelete, onEdit, onDelete }) => {
    const IconComponent = categoria.tipo === 'Receita' ? TrendingUp : TrendingDown;
    const bgColor = categoria.tipo === 'Receita' ? 'from-green-50 to-green-100' : 'from-red-50 to-red-100';
    const iconBg = categoria.tipo === 'Receita' ? 'bg-green-500' : 'bg-red-500';

    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group">
            <div className={`relative bg-gradient-to-br ${bgColor} p-2 sm:p-4`}>
                <div className="flex items-start space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                        <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full ${iconBg} flex items-center justify-center shadow-md`}
                             style={{ backgroundColor: categoria.cor || (categoria.tipo === 'Receita' ? '#10b981' : '#ef4444') }}>
                            <IconComponent className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {categoria.nome}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                categoria.tipo === 'Receita' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                            }`}>
                                {categoria.tipo}
                            </span>
                        </p>
                        {categoria.descricao && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                {categoria.descricao}
                            </p>
                        )}
                    </div>
                    <div className="flex-shrink-0">
                        {categoria.ativo !== false ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                        )}
                    </div>
                </div>
            </div>

            <div className="p-2 sm:p-4 space-y-2 sm:space-y-3">
                {categoria.subcategorias && categoria.subcategorias.length > 0 && (
                    <div className="text-xs sm:text-sm text-gray-600">
                        <span className="font-semibold">{categoria.subcategorias.length}</span> subcategoria(s)
                    </div>
                )}

                <div className="flex items-center justify-end pt-2 sm:pt-3 border-t border-gray-200 space-x-2">
                    {canEdit && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(categoria);
                            }}
                            className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors duration-200"
                            title="Editar"
                        >
                            <SquarePen className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                    {canDelete && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(categoria.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="Desativar"
                        >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const ListaCategorias = ({
    data,
    canCreate,
    canEdit,
    canDelete,
    handleOpenCreateModal,
    handleOpenEditModal,
    handleDelete
}) => {
    const { searchTerm } = useSearch();

    // Filtrar dados baseado na busca
    const filteredData = React.useMemo(() => {
        if (!searchTerm) return data;
        const lowerCaseSearch = searchTerm.toLowerCase();
        return data.filter(categoria =>
            categoria.nome?.toLowerCase().includes(lowerCaseSearch) ||
            categoria.tipo?.toLowerCase().includes(lowerCaseSearch) ||
            categoria.descricao?.toLowerCase().includes(lowerCaseSearch)
        );
    }, [data, searchTerm]);

    return (
        <div className="mt-4">
            <div className="flex justify-end items-center mb-4">
                {canCreate && (
                    <PrimaryButton
                        onClick={handleOpenCreateModal}
                        className="w-full sm:w-auto"
                    >
                        <span className="hidden sm:inline">+ Nova Categoria</span>
                        <span className="sm:hidden">+ Nova</span>
                    </PrimaryButton>
                )}
            </div>

            {filteredData.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                    <p className="text-gray-500 text-sm sm:text-lg">
                        {searchTerm
                            ? `Nenhuma categoria encontrada para "${searchTerm}".`
                            : 'Nenhuma categoria cadastrada.'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Cards para Mobile */}
                    <div className="grid grid-cols-1 md:hidden gap-2 sm:gap-4">
                        {filteredData.map((categoria) => (
                            <CategoriaCard
                                key={categoria.id}
                                categoria={categoria}
                                canEdit={canEdit}
                                canDelete={canDelete}
                                onEdit={handleOpenEditModal}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>

                    {/* Tabela para Desktop */}
                    <div className="hidden md:block bg-white shadow overflow-hidden sm:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Nome
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Descrição
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Subcategorias
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.map((categoria) => (
                                    <tr key={categoria.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {categoria.nome}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                categoria.tipo === 'Receita'
                                                    ? 'bg-green-100 text-green-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {categoria.tipo}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500 max-w-xs overflow-hidden truncate">
                                            {categoria.descricao || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {categoria.subcategorias?.length || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {categoria.ativo !== false ? (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                    Ativa
                                                </span>
                                            ) : (
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                    Inativa
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {canEdit && (
                                                <span
                                                    onClick={() => handleOpenEditModal(categoria)}
                                                    className="text-emerald-600 hover:text-emerald-900 cursor-pointer mr-3"
                                                >
                                                    Editar
                                                </span>
                                            )}
                                            {canDelete && (
                                                <span
                                                    onClick={() => handleDelete(categoria.id)}
                                                    className="text-red-600 hover:text-red-900 cursor-pointer"
                                                >
                                                    Desativar
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default ListaCategorias;

